import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Users, ClipboardList, LogOut, Menu, X } from "./icons";
import { useAuth } from "../../auth/AuthContext";
import futureHealthLogo from "../assets/homescreen/futurehealthlogo.svg";
import "../styles/CCSidebar.css";

/**
 * CCSidebar
 * Persistent left navigation used across every Care Coordinator page.
 * - Position stays constant (sticky) while the page content scrolls —
 *   the sidebar itself never moves.
 * - Desktop/tablet: full sidebar. Mobile (<=768px): slide-in drawer.
 * - Active-page highlighting is computed from the current pathname so
 *   nested routes (e.g. a patient detail page) still light up the right
 *   nav item, not just exact-path matches.
 * - userName/userRole are optional overrides. If not passed, they're
 *   pulled from the logged-in user in AuthContext — nothing is hardcoded.
 * - super_admin gets a restricted nav: no "New Inquiry Registration", and
 *   "Home" points to /admin/care-coordinators instead of /care-coordinator.
 *
 * Props:
 *  - userName      (string, optional) overrides the name pulled from AuthContext
 *  - userRole      (string, optional) overrides the role label pulled from AuthContext
 *  - onLogout      (fn)      optional — called AFTER the session is
 *                  cleared, in case a parent needs to react (e.g. reset
 *                  some other state). Navigation to /caretaker-login always
 *                  happens regardless of whether this is passed.
 *  - backLink      ({label, to})  optional — shown above the logo, used for
 *                  admin drill-down views (e.g. "← All Care Coordinators")
 */

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  care_coordinator: "Care Coordinator",
};

export default function CCSidebar({
  userName,
  userRole,
  onLogout,
  backLink = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const isSuperAdmin = user?.role === "super_admin";

  // Dynamic name/role — props (if explicitly passed by a parent) still win,
  // otherwise fall back to whoever is actually logged in.
  const displayName = userName || user?.name || "User";
  const displayRole = userRole || ROLE_LABELS[user?.role] || "Care Coordinator";

  // Highlighting is driven by a single computed activeKey (most-specific
  // route wins) rather than per-item checks, so exactly one nav item is
  // ever highlighted at a time.
  const pathname = location.pathname;
  let activeKey = null;
  if (pathname.startsWith("/care-coordinator/inquiry")) {
    activeKey = "register";
  } else if (pathname.startsWith("/care-coordinator/program")) {
    activeKey = "overview";
  } else if (pathname === "/care-coordinator") {
    activeKey = "home";
  } else if (pathname.startsWith("/admin/care-coordinators")) {
    activeKey = "home";
  }

  // super_admin only gets Home (-> /admin/care-coordinators) and the
  // Patient Programme Overview button. No New Inquiry Registration, and
  // Home does not point at /care-coordinator for this role.
  const navItems = isSuperAdmin
  ? [
      {
        key: "home",
        label: "Home",
        icon: Home,
        path: "/admin/care-coordinators",
      },
      {
        key: "overview",
        label: "Patient Programme Overview",
        icon: Users,
        path: "/care-coordinator/program",
        state: location.state,
      },
    ]
  : [
      {
        key: "home",
        label: "Home",
        icon: Home,
        path: "/care-coordinator",
      },
      {
        key: "overview",
        label: "Patient Programme Overview",
        icon: Users,
        path: "/care-coordinator/program",
      },
      {
        key: "register",
        label: "New Inquiry Registration",
        icon: ClipboardList,
        path: "/care-coordinator/inquiry",
      },
    ];


  const initials = displayName
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setOpen(false);
    setShowLogoutConfirm(false);
    // Actually terminate the session — clears the stored token/user so
    // ProtectedRoute stops treating this browser as logged in.
    logout();
    if (onLogout) onLogout();
    navigate("/caretaker-login", { replace: true });
  };

  return (
    <>
      {/* Mobile-only hamburger toggle */}
      <button
        className="cc-mobile-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close menu" : "Open menu"}
      >
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile-only backdrop, closes drawer on tap */}
      {open && <div className="cc-sidebar-backdrop" onClick={() => setOpen(false)} />}

      <aside className={`cc-sidebar${open ? " open" : ""}`}>
        {backLink && (
          <button className="cc-sidebar-backlink" onClick={() => navigate(backLink.to)}>
            ← {backLink.label}
          </button>
        )}

        <div className="cc-sidebar-logo-row">
          <img src={futureHealthLogo} alt="Future Health" className="cc-logo-img" />
          <div className="cc-logo-textcol">
            <span className="cc-logo-title">Future Health</span>
            <span className="cc-logo-sub">Care Coordinator Portal</span>
          </div>
        </div>

        <div className="cc-sidebar-divider" />

        <nav className="cc-sidebar-nav">
          {navItems.map(({ key, label, icon: Icon, path }) => {
            const active = key === activeKey;
            return (
              <NavLink
                  key={key}
                  to={path}
                  state={
                      key === "overview"
                          ? location.state
                          : undefined
                  }
                end
                onClick={() => setOpen(false)}
                className={`cc-nav-item${active ? " active" : ""}`}
              >
                <Icon size={20} className="cc-nav-icon" />
                <span>{label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="cc-sidebar-bottom">
          <div className="cc-user-card">
            <div className="cc-user-avatar">{initials || "CC"}</div>
            <div className="cc-user-info">
              <span className="cc-user-name">{displayName}</span>
              <span className="cc-user-role">{displayRole}</span>
            </div>
          </div>
          <button className="cc-logout-btn" onClick={handleLogoutClick}>
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ── Logout confirmation modal ── */}
      {showLogoutConfirm && (
        <div className="cc-logout-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="cc-logout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cc-logout-icon">!</div>
            <h3 className="cc-logout-title">Logout</h3>
            <p className="cc-logout-text">
              Are you sure you want to log out? You'll need to sign in again to access the portal.
            </p>
            <div className="cc-logout-actions">
              <button className="cc-logout-cancel-btn" onClick={() => setShowLogoutConfirm(false)}>
                Cancel
              </button>
              <button className="cc-logout-confirm-btn" onClick={confirmLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}